import {
  Model,
  Cesium3DTileFeature,
  Cesium3DTileset,
  ClippingPlane,
  Cartesian3,
} from "@cesium/engine";
import DitapPopup from "./DitapPopup";
import AnalysisClipping from "../analysis/AnalysisClipping";
import Utils from "../basic/Utils";
import DitapAlert from "./DitapAlert";

/**
 * 슬라이더 range 색상 적용.</br>
 * Slider percentage color applied
 * @private
 * @param {Element} input
 */
function slider(input) {
  const min = Number(input.min);
  const max = Number(input.max);
  const value = Number(input.value);
  const percentage = ((value - min) / (max - min)) * 100;
  input.style.backgroundImage =
    "linear-gradient(to right, #EEC252 0%, #EEC252 " +
    `${percentage}` +
    "%, #AAA " +
    `${percentage}` +
    "%, #AAA 100%)";
}

/**
 * 절단면 분석 팝업 클래스.</br>
 * AnalysisClipping popup class. </br>
 * Note: ModelGraphic은 setXMinValue, setXMaxValue, setYMinValue, setYMaxValue, setZMinValue, setZMaxValue 함수를 통해 범위 설정 필요. <br>
 * Note: ModelGraphic requires range setting through setXMinValue, setXMaxValue, setYMinValue, setYMaxValue, setZMinValue, setZMaxValue functions.
 * @alias PopupAnalysisClipping
 * @extends DitapPopup
 *
 * @constructor
 * @param {AnalysisClipping} analysisClipping 절단면 분석 대상 모델.</br> The model targeted for clipping plane analysis.
 * @param {DitapPaneManager} ditapPaneManager
 * @param {object} options
 * @param {function} [options=closeCallback] 인스턴스 종료 콜백
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
 *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
 * );
 * const model = Ditap.Model.fromGltf({
 *  id: "model_01",
 *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *  modelMatrix: modelMatrix,
 *  scale: 3,
 * });
 * viewer.scene.primitives.add(model);
 *
 * // 1. AnalysisClipping 인스턴스 생성
 * const analysisClipping = new AnalysisClipping(viewer, model);
 *
 * // 2. AnalysisClipping 인스턴스 할당
 * const popupAnalysisClipping = new Ditap.PopupAnalysisClipping(analysisClipping, viewer.ditapPaneManager);
 * popupAnalysisClipping.open();
 *
 */
class PopupAnalysisClipping extends DitapPopup {
  constructor(analysisClipping, ditapPaneManager, options = {}) {
    const parent = document.body;
    super(parent, ditapPaneManager);

    this._planeX = undefined;
    this._originalPlaneX = undefined;
    this._planeY = undefined;
    this._originalPlaneY = undefined;
    this._planeZ = undefined;
    this._originalPlaneZ = undefined;
    this._buttonElement = undefined;

    this._analysisClipping = undefined;
    if (analysisClipping instanceof AnalysisClipping) {
      this._analysisClipping = analysisClipping;
    } else {
      throw Error("PopupAnalysisClipping analysisClipping type error");
    }

    // 종료 콜백
    this._closeCallback = undefined;
    if (typeof options?.closeCallback === "function") {
      this._closeCallback = options.closeCallback;
    }

    // 절단면 distance 범위
    this._radius = undefined;
    if (typeof options?.radius === "number") {
      this._radius = options.radius;
    }

    // 팝업 생성
    const infoBox = this.createInfoBox();
    // 헤더 추가
    this.prependHeader(infoBox["header"]["title"]);
    // 컨텐츠 추가
    this.appendSubTitle(infoBox["content"]["subTitle"]);
    this.appendContent(infoBox["content"]["clippingCheckList"]);
    this.appendContent(infoBox["content"]["clippingRangeList"]);
    // 팝업 설정
    this.wrapper.classList.remove("ditap-size-popup");
    this.footer.remove();

    this.changeCloseBtn();
    const isMobile = Utils.isBodyWidthLessThan600();
    if (!isMobile) {
      this.enableDrag();
      this.enableTouch();
    }

    this._closeAlert = this.createCloseAlert();

    // 노드 세팅
    this._clippingCheckX = infoBox["content"]["clippingCheckX"];
    this._clippingCheckXCheckBox = infoBox["content"]["clippingCheckXCheckBox"];
    this._clippingCheckY = infoBox["content"]["clippingCheckY"];
    this._clippingCheckYCheckBox = infoBox["content"]["clippingCheckYCheckBox"];
    this._clippingCheckZ = infoBox["content"]["clippingCheckZ"];
    this._clippingCheckZCheckBox = infoBox["content"]["clippingCheckZCheckBox"];
    this._clippingRangeX = infoBox["content"]["clippingRangeX"];
    this._clippingRangeXMinValue = infoBox["content"]["clippingRangeXMinValue"];
    this._clippingRangeXCurrentValue =
      infoBox["content"]["clippingRangeXCurrentValue"];
    this._clippingRangeXMaxValue = infoBox["content"]["clippingRangeXMaxValue"];
    this._clippingRangeXMinusP = infoBox["content"]["clippingRangeXMinusP"];
    this._clippingRangeXPlusP = infoBox["content"]["clippingRangeXPlusP"];
    this._clippingRangeXSlider = infoBox["content"]["clippingRangeXSlider"];
    this._clippingRangeY = infoBox["content"]["clippingRangeY"];
    this._clippingRangeYMinValue = infoBox["content"]["clippingRangeYMinValue"];
    this._clippingRangeYCurrentValue =
      infoBox["content"]["clippingRangeYCurrentValue"];
    this._clippingRangeYMaxValue = infoBox["content"]["clippingRangeYMaxValue"];
    this._clippingRangeYSlider = infoBox["content"]["clippingRangeYSlider"];
    this._clippingRangeYMinusP = infoBox["content"]["clippingRangeYMinusP"];
    this._clippingRangeYPlusP = infoBox["content"]["clippingRangeYPlusP"];
    this._clippingRangeZ = infoBox["content"]["clippingRangeZ"];
    this._clippingRangeZMinValue = infoBox["content"]["clippingRangeZMinValue"];
    this._clippingRangeZCurrentValue =
      infoBox["content"]["clippingRangeZCurrentValue"];
    this._clippingRangeZMaxValue = infoBox["content"]["clippingRangeZMaxValue"];
    this._clippingRangeZSlider = infoBox["content"]["clippingRangeZSlider"];
    this._clippingRangeZMinusP = infoBox["content"]["clippingRangeZMinusP"];
    this._clippingRangeZPlusP = infoBox["content"]["clippingRangeZPlusP"];

    // // 윤곽선
    // const silhouette = PostProcessStageLibrary.createEdgeDetectionStage();
    // silhouette.uniforms.color = Color.BLUE;
    // silhouette.uniforms.length = 0.01;
    // silhouette.selected = [];
    // const postprocessStage = PostProcessStageLibrary.createSilhouetteStage([silhouette]);
    // viewer.scene.postProcessStages.add(postprocessStage);
    // this._postprocessStage = postprocessStage;
    // this._silhouette = silhouette;

    // Mouse Hover 이벤트
    //this.createMouseMoveHandler();

    // 팝업 이벤트 설정
    this.setEvent();

    // 팝업 내용 업데이트
    this.update();

    // this.createResizeEvent();
  }

  set closeCallback(closeCallback) {
    if (typeof closeCallback === "function") {
      this._closeCallback = closeCallback;
    } else {
      this._closeCallback = undefined;
    }
  }
  get closeCallback() {
    return this._closeCallback;
  }

  /**
   * 선택 모델.</br>
   * Select model
   * @readonly
   * @type {AnalysisClipping}
   */
  get analysisClipping() {
    return this._analysisClipping;
  }
  set analysisClipping(analysisClipping) {
    if (analysisClipping instanceof AnalysisClipping) {
      this._analysisClipping = analysisClipping;
      this.update();
    } else {
      this._analysisClipping = undefined;
    }
  }

  /**
   * X축 절단면 엔티티.</br>
   * X axis ClippingPlane entity
   * @readonly
   * @type {Entity}
   */
  set planeX(planeX) {
    this._planeX = planeX;
  }
  get planeX() {
    return this._planeX;
  }

  /**
   * Y축 절단면 엔티티.</br>
   * Y axis ClippingPlane entity
   * @readonly
   * @type {Entity}
   */
  set planeY(planeY) {
    this._planeY = planeY;
  }
  get planeY() {
    return this._planeY;
  }

  /**
   * Z축 절단면 엔티티.</br>
   * Z axis ClippingPlane entity
   * @readonly
   * @type {Entity}
   */
  set planeZ(planeZ) {
    this._planeZ = planeZ;
  }
  get planeZ() {
    return this._planeZ;
  }

  /**
   * X축 체크박스를 감싸는 요소.</br>
   * X axis checkbox wrapper
   * @readonly
   * @type {Element}
   */
  set clippingCheckX(clippingCheckX) {
    this._clippingCheckX = clippingCheckX;
  }
  get clippingCheckX() {
    return this._clippingCheckX;
  }

  /**
   * X축 체크박스.</br>
   * X axis checkbox
   * @readonly
   * @type {Element}
   */
  set clippingCheckXCheckBox(clippingCheckXCheckBox) {
    this._clippingCheckXCheckBox = clippingCheckXCheckBox;
  }
  get clippingCheckXCheckBox() {
    return this._clippingCheckXCheckBox;
  }

  /**
   *  Y축 체크박스를 감싸는 요소.</br>
   * Y axis checkbox wrapper
   * @readonly
   * @type {Element}
   */
  set clippingCheckY(clippingCheckY) {
    this._clippingCheckY = clippingCheckY;
  }
  get clippingCheckY() {
    return this._clippingCheckY;
  }

  /**
   * Y axis checkbox
   * Y축 체크박스.</br>
   * @readonly
   * @type {Element}
   */
  set clippingCheckYCheckBox(clippingCheckYCheckBox) {
    this._clippingCheckYCheckBox = clippingCheckYCheckBox;
  }
  get clippingCheckYCheckBox() {
    return this._clippingCheckYCheckBox;
  }

  /**
   *  Y축 체크박스를 감싸는 요소.</br>
   * Z axis checkbox wrapper
   * @readonly
   * @type {Element}
   */
  set clippingCheckZ(clippingCheckZ) {
    this._clippingCheckZ = clippingCheckZ;
  }
  get clippingCheckZ() {
    return this._clippingCheckZ;
  }

  /**
   * Z축 체크박스 요소.<br>
   * Z axis checkbox
   * @readonly
   * @type {Element}
   */
  set clippingCheckZCheckBox(clippingCheckZCheckBox) {
    this._clippingCheckZCheckBox = clippingCheckZCheckBox;
  }
  get clippingCheckZCheckBox() {
    return this._clippingCheckZCheckBox;
  }

  /**
   * X축 슬라이더 감싸는 요소.</br>
   * X axis slider wrapper
   * @readonly
   * @type {Element}
   */
  set clippingRangeX(clippingRangeX) {
    this._clippingRangeX = clippingRangeX;
  }
  get clippingRangeX() {
    return this._clippingRangeX;
  }

  /**
   * X축 슬라이더 최소값 텍스트 요소.</br>
   * X axis silider minimum text element
   * @readonly
   * @type {Element}
   */
  set clippingRangeXMinValue(clippingRangeXMinValue) {
    this._clippingRangeXMinValue = clippingRangeXMinValue;
  }
  get clippingRangeXMinValue() {
    return this._clippingRangeXMinValue;
  }

  /**
   * X축 슬라이더 현재값 텍스트 요소.</br>
   * X axis silider current text element
   * @readonly
   * @type {Element}
   */
  set clippingRangeXCurrentValue(clippingRangeXCurrentValue) {
    this._clippingRangeXCurrentValue = clippingRangeXCurrentValue;
  }
  get clippingRangeXCurrentValue() {
    return this._clippingRangeXCurrentValue;
  }

  /**
   * X축 슬라이더 최대값 텍스트 요소.</br>
   * X axis silider maximum text element
   * @readonly
   * @type {Element}
   */
  set clippingRangeXMaxValue(clippingRangeXMaxValue) {
    this._clippingRangeXMaxValue = clippingRangeXMaxValue;
  }
  get clippingRangeXMaxValue() {
    return this._clippingRangeXMaxValue;
  }

  /**
   * X축 슬라이더 요소.</br>
   * X axis slider
   * @readonly
   * @type {Element}
   */
  set clippingRangeXSlider(clippingRangeXSlider) {
    this._clippingRangeXSlider = clippingRangeXSlider;
  }
  get clippingRangeXSlider() {
    return this._clippingRangeXSlider;
  }

  /**
   * X축 슬라이더 마이너스 버튼 요소.</br>
   * X axis slider minus button
   * @readonly
   * @type {Element}
   */
  set clippingRangeXMinusP(clippingRangeXMinusP) {
    this._clippingRangeXMinusP = clippingRangeXMinusP;
  }
  get clippingRangeXMinusP() {
    return this._clippingRangeXMinusP;
  }

  /**
   * X축 슬라이더 플러스 버튼 요소.</br>
   * X axis slider plus button
   * @readonly
   * @type {Element}
   */
  set clippingRangeXPlusP(clippingRangeXPlusP) {
    this._clippingRangeXPlusP = clippingRangeXPlusP;
  }
  get clippingRangeXPlusP() {
    return this._clippingRangeXPlusP;
  }

  /**
   * Y축 슬라이더 감싸는 요소.</br>
   * Y axis slider wrapper
   * @readonly
   * @type {Element}
   */
  set clippingRangeY(clippingRangeY) {
    this._clippingRangeY = clippingRangeY;
  }
  get clippingRangeY() {
    return this._clippingRangeY;
  }

  /**
   * Y축 슬라이더 최소값 텍스트 요소.</br>
   * Y axis silider minimum text element
   * @readonly
   * @type {Element}
   */
  set clippingRangeYMinValue(clippingRangeYMinValue) {
    this._clippingRangeYMinValue = clippingRangeYMinValue;
  }
  get clippingRangeYMinValue() {
    return this._clippingRangeYMinValue;
  }

  /**
   * Y축 슬라이더 현재값 텍스트 요소.</br>
   * Y axis silider current text element
   * @readonly
   * @type {Element}
   */
  set clippingRangeYCurrentValue(clippingRangeYCurrentValue) {
    this._clippingRangeYCurrentValue = clippingRangeYCurrentValue;
  }
  get clippingRangeYCurrentValue() {
    return this._clippingRangeYCurrentValue;
  }

  /**
   * Y축 슬라이더 최대값 텍스트 요소.</br>
   * Y axis silider maximum text element
   * @readonly
   * @type {Element}
   */
  set clippingRangeYMaxValue(clippingRangeYMaxValue) {
    this._clippingRangeYMaxValue = clippingRangeYMaxValue;
  }
  get clippingRangeYMaxValue() {
    return this._clippingRangeYMaxValue;
  }

  /**
   * Y축 슬라이더 요소.</br>
   * Y axis slider
   * @readonly
   * @type {Element}
   */
  set clippingRangeYSlider(clippingRangeYSlider) {
    this._clippingRangeYSlider = clippingRangeYSlider;
  }
  get clippingRangeYSlider() {
    return this._clippingRangeYSlider;
  }

  /**
   * Y축 슬라이더 마이너스 버튼 요소.</br>
   * Y axis slider minus button
   * @readonly
   * @type {Element}
   */
  set clippingRangeYMinusP(clippingRangeYMinusP) {
    this._clippingRangeYMinusP = clippingRangeYMinusP;
  }
  get clippingRangeYMinusP() {
    return this._clippingRangeYMinusP;
  }

  /**
   * Y축 슬라이더 플러스 버튼 요소.</br>
   * Y axis slider plus button
   * @readonly
   * @type {Element}
   */
  set clippingRangeYPlusP(clippingRangeYPlusP) {
    this._clippingRangeYPlusP = clippingRangeYPlusP;
  }
  get clippingRangeYPlusP() {
    return this._clippingRangeYPlusP;
  }

  /**
   * Z축 슬라이더 감싸는 요소.</br>
   * Z axis slider wrapper
   * @readonly
   * @type {Element}
   */
  set clippingRangeZ(clippingRangeZ) {
    this._clippingRangeZ = clippingRangeZ;
  }
  get clippingRangeZ() {
    return this._clippingRangeZ;
  }

  /**
   * Z축 슬라이더 최소값 텍스트 요소.</br>
   * Z axis silider minimum text element
   * @readonly
   * @type {Element}
   */
  set clippingRangeZMinValue(clippingRangeZMinValue) {
    this._clippingRangeZMinValue = clippingRangeZMinValue;
  }
  get clippingRangeZMinValue() {
    return this._clippingRangeZMinValue;
  }

  /**
   * Z축 슬라이더 현재값 텍스트 요소.</br>
   * Z axis silider current text element
   * @readonly
   * @type {Element}
   */
  set clippingRangeZCurrentValue(clippingRangeZCurrentValue) {
    this._clippingRangeZCurrentValue = clippingRangeZCurrentValue;
  }
  get clippingRangeZCurrentValue() {
    return this._clippingRangeZCurrentValue;
  }

  /**
   * Z축 슬라이더 최대값 텍스트 요소.</br>
   * Z axis silider maximum text element
   * @readonly
   * @type {Element}
   */
  set clippingRangeZMaxValue(clippingRangeZMaxValue) {
    this._clippingRangeZMaxValue = clippingRangeZMaxValue;
  }
  get clippingRangeZMaxValue() {
    return this._clippingRangeZMaxValue;
  }

  /**
   * Z축 슬라이더 요소.</br>
   * Z axis silider
   * @readonly
   * @type {Element}
   */
  set clippingRangeZSlider(clippingRangeZSlider) {
    this._clippingRangeZSlider = clippingRangeZSlider;
  }
  get clippingRangeZSlider() {
    return this._clippingRangeZSlider;
  }

  /**
   * Z축 슬라이더 마이너스 버튼 요소.</br>
   * Z axis silider minus button
   * @readonly
   * @type {Element}
   */
  set clippingRangeZMinusP(clippingRangeZMinusP) {
    this._clippingRangeZMinusP = clippingRangeZMinusP;
  }
  get clippingRangeZMinusP() {
    return this._clippingRangeZMinusP;
  }

  /**
   * Z축 슬라이더 플러스 버튼 요소.</br>
   * Z axis silider plus button
   * @readonly
   * @type {Element}
   */
  set clippingRangeZPlusP(clippingRangeZPlusP) {
    this._clippingRangeZPlusP = clippingRangeZPlusP;
  }
  get clippingRangeZPlusP() {
    return this._clippingRangeZPlusP;
  }

  /**
   * 버튼 요소.</br>
   * Button element
   * @readonly
   * @type {Element}
   */
  get buttonElement() {
    return this._buttonElement;
  }
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }

  /**
   * 절단면 상태값 초기화. <br>
   * Reset clipping plane information.
   */
  rollback() {
    // X축 절단면
    if (this._originalPlaneX && this.planeX) {
      const distnace = this._originalPlaneX.distance;
      const normal = Cartesian3.clone(
        this._originalPlaneX.normal,
        new Cartesian3()
      );
      this.analysisClipping.setNormal(this.planeX, normal);
      this.analysisClipping.setDistance(this.planeX, distnace);

      this.setXCurrentValue(distnace);
    } else if (this._originalPlaneX && !this.planeX) {
      const distnace = this._originalPlaneX.distance;
      const normal = Cartesian3.clone(
        this._originalPlaneX.normal,
        new Cartesian3()
      );
      this.planeX = this.analysisClipping.add(normal, distnace);

      this.setXCurrentValue(distnace);
    } else if (!this._originalPlaneX && this.planeX) {
      this.analysisClipping.remove(this.planeX);
      this.planeX = undefined;
    }

    // Y축 절단면
    if (this._originalPlaneY && this.planeY) {
      const distnace = this._originalPlaneY.distance;
      const normal = Cartesian3.clone(
        this._originalPlaneY.normal,
        new Cartesian3()
      );
      this.analysisClipping.setNormal(this.planeY, normal);
      this.analysisClipping.setDistance(this.planeY, distnace);

      this.setYCurrentValue(distnace);
    } else if (this._originalPlaneY && !this.planeY) {
      const distnace = this._originalPlaneY.distance;
      const normal = Cartesian3.clone(
        this._originalPlaneY.normal,
        new Cartesian3()
      );
      this.planeY = this.analysisClipping.add(normal, distnace);

      this.setYCurrentValue(distnace);
    } else if (!this._originalPlaneY && this.planeY) {
      this.analysisClipping.remove(this.planeY);
      this.planeY = undefined;
    }

    // Z축 절단면
    if (this._originalPlaneZ && this.planeZ) {
      const distnace = this._originalPlaneZ.distance;
      const normal = Cartesian3.clone(
        this._originalPlaneZ.normal,
        new Cartesian3()
      );
      this.analysisClipping.setNormal(this.planeZ, normal);
      this.analysisClipping.setDistance(this.planeZ, distnace);

      this.setZCurrentValue(distnace);
    } else if (this._originalPlaneZ && !this.planeZ) {
      const distnace = this._originalPlaneZ.distance;
      const normal = Cartesian3.clone(
        this._originalPlaneZ.normal,
        new Cartesian3()
      );
      this.planeZ = this.analysisClipping.add(normal, distnace);

      this.setZCurrentValue(distnace);
    } else if (!this._originalPlaneZ && this.planeZ) {
      this.analysisClipping.remove(this.planeZ);
      this.planeZ = undefined;
    }
  }

  /**
   * 팝업 인스턴스 초기화.</br>
   * Reset this object
   *
   * @param {boolean} [removePlane=true] 설정된 절단면 제거 여부. <br> Whether to remove the set cutting plane.
   */
  reset(removePlane = true) {
    // 체크박스 초기화
    this.setXCheckBox(false);
    this.setYCheckBox(false);
    this.setZCheckBox(false);

    // 슬라이드바 초기화
    this.setXMinValue(0); // X 최소값 초기화
    this.setXMaxValue(0); // X 최대값 초기화
    this.setYMinValue(0); // Y 최소값 초기화
    this.setYMaxValue(0); // Y 최대값 초기화
    this.setZMinValue(0); // Z 최소값 초기화
    this.setZMaxValue(0); // Z 최대값 초기화
    this.setXSliderValue(0); // X 슬라이더 값 초기화
    this.setYSliderValue(0); // Y 슬라이더 값 초기화
    this.setZSliderValue(0); // Z 슬라이더 값 초기화
    this.hideRange("X"); // X 슬라이더 숨김
    this.hideRange("Y"); // Y 슬라이더 숨김
    this.hideRange("Z"); // Z 슬라이더 숨김

    // object에 추가된 PopupAnalysisClipiing ClippingPlane 제거
    if (removePlane) {
      if (this.planeX instanceof ClippingPlane) {
        this.analysisClipping.remove(this.planeX);
      }
      if (this.planeY instanceof ClippingPlane) {
        this.analysisClipping.remove(this.planeY);
      }
      if (this.planeZ instanceof ClippingPlane) {
        this.analysisClipping.remove(this.planeZ);
      }
    }

    this.planeX = undefined;
    this.planeY = undefined;
    this.planeZ = undefined;
  }

  /**
   * 팝업과 절단면 파괴.</br>
   * Destroy popup and ClippingPlane
   * @param {boolean} [removePlane=true] 설정된 절단면 제거 여부. <br> Whether to remove the set cutting plane.
   */
  destroy(removePlane = true) {
    // object의 ClippingPlane도 같이 제거함
    this.reset(removePlane);
    super.destroy();
  }

  /**
   * 선택 객체 정보 업데이트. <br>
   * Select object information update. <br>
   * ※ 주의사항(Note). <br>
   * rollback 내용도 초기화 됩니다. <br>
   * Rollback contents are also initialized.
   */
  update() {
    const selectObject = this.analysisClipping.selectObject;

    // object boundingsphere radius 추출
    let radius;
    if (this._radius) {
      radius = this._radius;
    } else if (
      selectObject instanceof Model ||
      selectObject instanceof Cesium3DTileset ||
      selectObject instanceof Cesium3DTileFeature
    ) {
      radius = Math.ceil(selectObject.boundingSphere.radius);
    } else {
      radius = 0;
    }

    let min = -radius;
    let max = radius;
    let planeXValue = 0;
    let planeYValue = 0;
    let planeZValue = 0;

    // 기본 평면 설정
    this.planeX = this.analysisClipping.getPlaneByNormal(
      AnalysisClipping.PLANE_X_NORMAL
    );
    if (this.planeX) {
      this._originalPlaneX = ClippingPlane.clone(
        this.planeX,
        new ClippingPlane(new Cartesian3(), 0)
      );

      const distance = this.planeX.distance;
      min = Math.min(min, distance);
      max = Math.max(max, distance);
      planeXValue = distance;

      this.setXCheckBox(true);
      this.showRange("X");
    }

    this.planeY = this.analysisClipping.getPlaneByNormal(
      AnalysisClipping.PLANE_Y_NORMAL
    );
    if (this.planeY) {
      this._originalPlaneY = ClippingPlane.clone(
        this.planeY,
        new ClippingPlane(new Cartesian3(), 0)
      );

      const distance = this.planeY.distance;
      min = Math.min(min, distance);
      max = Math.max(max, distance);
      planeYValue = distance;

      this.setYCheckBox(true);
      this.showRange("Y");
    }

    this.planeZ = this.analysisClipping.getPlaneByNormal(
      AnalysisClipping.PLANE_Z_NORMAL
    );
    if (this.planeZ) {
      this._originalPlaneZ = ClippingPlane.clone(
        this.planeZ,
        new ClippingPlane(new Cartesian3(), 0)
      );

      const distance = this.planeZ.distance;
      min = Math.min(min, distance);
      max = Math.max(max, distance);
      planeZValue = distance;

      this.setZCheckBox(true);
      this.showRange("Z");
    }

    // ClippingPlane distance 슬라이더 설정
    this.setXMinValue(min);
    this.setXMaxValue(max);
    this.setYMinValue(min);
    this.setYMaxValue(max);
    this.setZMinValue(min);
    this.setZMaxValue(max);
    this.setXCurrentValue(planeXValue);
    this.setYCurrentValue(planeYValue);
    this.setZCurrentValue(planeZValue);
    this.setXSliderValue(planeXValue);
    this.setYSliderValue(planeYValue);
    this.setZSliderValue(planeZValue);
  }

  /**
   * X축 체크박스 설정</br>
   * Set X axis checkbox
   * @param {boolean} value 체크 여부.</br> Check value
   */
  setXCheckBox(state) {
    this.clippingCheckXCheckBox.checked = !state;
  }

  /**
   * Y축 체크박스 설정</br>
   * Set Y axis checkbox
   * @param {boolean} value 체크 여부.</br> Check value
   */
  setYCheckBox(state) {
    this.clippingCheckYCheckBox.checked = !state;
  }

  /**
   * Z축 체크박스 설정</br>
   * Set Z axis checkbox
   * @param {boolean} value 체크 여부.</br> Check value
   */
  setZCheckBox(state) {
    this.clippingCheckZCheckBox.checked = !state;
  }

  /**
   * X축 슬라이더 최소값 설정.</br>
   * Set X axis slider minimum text value
   * @param {number} value 슬라이더 최소값 (미터).</br> Slider minimum value (meters).
   */
  setXMinValue(value) {
    this.clippingRangeXMinValue.innerText = value;
    this.clippingRangeXSlider.setAttribute("min", value);
  }

  /**
   * X축 슬라이더 현재값 설정.</br>
   * Set X axis slider current text value
   * @param {number} value 슬라이더 현재값 (미터).</br> Slider current value (meters).
   */
  setXCurrentValue(value) {
    this.clippingRangeXCurrentValue.innerText = value;
  }

  /**
   * X축 슬라이더 최대값 설정.</br>
   * Set X axis slider maximum text value
   * @param {number} value 슬라이더 최대값 (미터).</br> Slider maximum value (meters).
   */
  setXMaxValue(value) {
    this.clippingRangeXMaxValue.innerText = value;
    this.clippingRangeXSlider.setAttribute("max", value);
  }

  /**
   * Y축 슬라이더 최소값 설정.</br>
   * Set Y axis slider minimum text value
   * @param {number} value 슬라이더 최소값 (미터).</br> Slider minimum value (meters).
   */
  setYMinValue(value) {
    this.clippingRangeYMinValue.innerText = value;
    this.clippingRangeYSlider.setAttribute("min", value);
  }

  /**
   * Y축 슬라이더 현재값 설정.</br>
   * Set Y axis slider current text value
   * @param {number} value 슬라이더 현재값 (미터).</br> Slider current value (meters).
   */
  setYCurrentValue(value) {
    this.clippingRangeYCurrentValue.innerText = value;
  }

  /**
   *  Y축 슬라이더 최대값 설정.</br>
   * Set Y axis slider maximum text value
   * @param {number} value 슬라이더 최대값 (미터).</br> Slider maximum value (meters).
   */
  setYMaxValue(value) {
    this.clippingRangeYMaxValue.innerText = value;
    this.clippingRangeYSlider.setAttribute("max", value);
  }

  /**
   * Z축 슬라이더 최소값 설정.</br>
   * Set Z axis slider minimum text value
   * @param {number} value 슬라이더 최소값 (미터).</br> Slider minimum value (meters).
   */
  setZMinValue(value) {
    this.clippingRangeZMinValue.innerText = value;
    this.clippingRangeZSlider.setAttribute("min", value);
  }

  /**
   * Z축 슬라이더 현재값 설정.</br>
   * Set Z axis slider current text value
   * @param {number} value 슬라이더 현재값 (미터).</br> Slider current value (meters).
   */
  setZCurrentValue(value) {
    this.clippingRangeZCurrentValue.innerText = value;
  }

  /**
   * Z축 슬라이더 최대값 설정.</br>
   * Set Z axis slider maximum text value
   * @param {number} value 슬라이더 최대값 (미터).</br> Slider maximum value (meters).
   */
  setZMaxValue(value) {
    this.clippingRangeZMaxValue.innerText = value;
    this.clippingRangeZSlider.setAttribute("max", value);
  }

  /**
   * X축 슬라이더 값 설정.</br>
   * Set X axis slider value
   * @param {number} value 슬라이더 값 (미터).</br> Slider value (meters).
   */
  setXSliderValue(value) {
    this.clippingRangeXSlider.value = value;
    slider(this.clippingRangeXSlider);
  }

  /**
   * Y축 슬라이더 값 설정.</br>
   * Set Y axis slider value
   * @param {number} value 슬라이더 값 (미터).</br> Slider value (meters).
   */
  setYSliderValue(value) {
    this.clippingRangeYSlider.value = value;
    slider(this.clippingRangeYSlider);
  }

  /**
   * Z축 슬라이더 값 설정.</br>
   * Set Z axis slider value
   * @param {number} value 슬라이더 값 (미터).</br> Slider value (meters).
   */
  setZSliderValue(value) {
    this.clippingRangeZSlider.value = value;
    slider(this.clippingRangeZSlider);
  }

  /**
   * 입력 축 슬라이더 래퍼 표시.</br>
   * Show input axis slider wrapper
   * @param {string} type ["X", "Y", "Z"]
   */
  showRange(type) {
    type = type.toUpperCase();
    switch (type) {
      case "X":
        this.clippingRangeX.classList.remove("hide");
        this.setXSliderValue(0);
        break;
      case "Y":
        this.clippingRangeY.classList.remove("hide");
        this.setYSliderValue(0);
        break;
      case "Z":
        this.clippingRangeZ.classList.remove("hide");
        this.setZSliderValue(0);
        break;
      default:
        break;
    }
  }

  /**
   * 입력 축 슬라이더 래퍼 숨김.</br>
   * Hide input axis slider wrapper
   * @param {string} type ["X", "Y", "Z"]
   */
  hideRange(type) {
    type = type.toUpperCase();
    switch (type) {
      case "X":
        this.clippingRangeX.classList.add("hide");
        break;
      case "Y":
        this.clippingRangeY.classList.add("hide");
        break;
      case "Z":
        this.clippingRangeZ.classList.add("hide");
        break;
      default:
        break;
    }
  }

  // /**
  //  *
  //  * Mouse Hover 이벤트
  //  */
  // createMouseMoveHandler() {
  //   const viewer = this.viewer;
  //   const handler = this.handler;

  //   handler.setInputAction((e) => {
  //     const selectFeature = viewer.scene.pick(e.startPosition);
  //     if (
  //       !this.selectObject &&
  //       defined(selectFeature) &&
  //       (selectFeature.primitive instanceof Model ||
  //         selectFeature instanceof Cesium3DTileset ||
  //         selectFeature instanceof Cesium3DTileFeature)
  //     ) {
  //       const hoverObject =
  //         selectFeature instanceof Cesium3DTileFeature
  //           ? selectFeature.tileset
  //           : selectFeature.primitive;

  //       this.silhouette.selected = [hoverObject];
  //     } else {
  //       this.silhouette.selected = [];
  //     }
  //   }, ScreenSpaceEventType.MOUSE_MOVE);
  // }

  /**
   * 종료 팝업 생성. <br> Create exit popup.
   * @returns {DitapAlert} ditapAlert 종료 팝업. <br> Exit pop-up.
   */
  createCloseAlert() {
    const alert = new DitapAlert({
      type: "question",
      title: "분석 종료",
      content1: "확인 : 분석 적용",
      content2: "취소 : 분석 초기화",
      buttons: "OKANDCANCEL",
      open: false,
      callback: () => {
        this.reset(false);
        this.close();
        if (typeof this.closeCallback === "function") {
          this._closeCallback(this, true);
        }
      },
      closeCallback: () => {
        this.rollback();
        this.reset(false);
        this.close();
        if (typeof this.closeCallback === "function") {
          this._closeCallback(this, false);
        }
      },
    });

    return alert;
  }

  /*
   * 팝업 이벤트 설정.</br>
   * Set popup event
   */
  setEvent() {
    // 팝업창 닫으면 ClippingPlane 초기화
    this.closeBtn.addEventListener("click", () => {
      this._closeAlert.open();
    });

    // X 체크 박스
    this.clippingCheckX.addEventListener("click", () => {
      const check = !this.clippingCheckXCheckBox.checked;
      this.clippingCheckXCheckBox.checked = check;

      if (this.planeX instanceof ClippingPlane) {
        this.analysisClipping.remove(this.planeX);
        this.planeX = undefined;
      }

      if (check) {
        this.hideRange("X");
      } else {
        this.planeX = this.analysisClipping.add(
          AnalysisClipping.PLANE_X_NORMAL
        );
        this.showRange("X");
      }
    });
    // Y 체크 박스
    this.clippingCheckY.addEventListener("click", () => {
      const check = !this.clippingCheckYCheckBox.checked;
      this.clippingCheckYCheckBox.checked = check;

      if (this.planeY instanceof ClippingPlane) {
        this.analysisClipping.remove(this.planeY);
        this.planeY = undefined;
      }

      if (check) {
        this.hideRange("Y");
      } else {
        this.planeY = this.analysisClipping.add(
          AnalysisClipping.PLANE_Y_NORMAL
        );
        this.showRange("Y");
      }
    });
    // Z 체크 박스
    this.clippingCheckZ.addEventListener("click", () => {
      const check = !this.clippingCheckZCheckBox.checked;
      this.clippingCheckZCheckBox.checked = check;

      if (this.planeZ instanceof ClippingPlane) {
        this.analysisClipping.remove(this.planeZ);
        this.planeZ = undefined;
      }

      if (check) {
        this.hideRange("Z");
      } else {
        this.planeZ = this.analysisClipping.add(
          AnalysisClipping.PLANE_Z_NORMAL
        );
        this.showRange("Z");
      }
    });
    // X 슬라이더
    this.clippingRangeXSlider.addEventListener("input", () => {
      let value = Number(this.clippingRangeXSlider.value);
      value = Number(value.toFixed(1));
      this.analysisClipping.setDistance(this.planeX, value);
      this.setXCurrentValue(value);
      slider(this.clippingRangeXSlider);
    });
    // X - 버튼
    this.clippingRangeXMinusP.addEventListener("click", () => {
      let value = Number(this.clippingRangeXSlider.value) - 0.1;
      value = Number(value.toFixed(1));
      this.analysisClipping.setDistance(this.planeX, value);
      this.setXSliderValue(value);
      this.setXCurrentValue(value);
    });
    // X + 버튼
    this.clippingRangeXPlusP.addEventListener("click", () => {
      let value = Number(this.clippingRangeXSlider.value) + 0.1;
      value = Number(value.toFixed(1));
      this.analysisClipping.setDistance(this.planeX, value);
      this.setXSliderValue(value);
      this.setXCurrentValue(value);
    });
    // Y 슬라이더
    this.clippingRangeYSlider.addEventListener("input", () => {
      let value = Number(this.clippingRangeYSlider.value);
      value = Number(value.toFixed(1));
      this.analysisClipping.setDistance(this.planeY, value);
      this.setYCurrentValue(value);
      slider(this.clippingRangeYSlider);
    });
    // Y - 버튼
    this.clippingRangeYMinusP.addEventListener("click", () => {
      let value = Number(this.clippingRangeYSlider.value) - 0.1;
      value = Number(value.toFixed(1));
      this.analysisClipping.setDistance(this.planeY, value);
      this.setYSliderValue(value);
      this.setYCurrentValue(value);
    });
    // Y + 버튼
    this.clippingRangeYPlusP.addEventListener("click", () => {
      let value = Number(this.clippingRangeYSlider.value) + 0.1;
      value = Number(value.toFixed(1));
      this.analysisClipping.setDistance(this.planeY, value);
      this.setYSliderValue(value);
      this.setYCurrentValue(value);
    });
    // Z 슬라이더
    this.clippingRangeZSlider.addEventListener("input", () => {
      let value = Number(this.clippingRangeZSlider.value);
      value = Number(value.toFixed(1));
      this.analysisClipping.setDistance(this.planeZ, value);
      this.setZCurrentValue(value);
      slider(this.clippingRangeZSlider);
    });
    // Z - 버튼
    this.clippingRangeZMinusP.addEventListener("click", () => {
      let value = Number(this.clippingRangeZSlider.value) - 0.1;
      value = Number(value.toFixed(1));
      this.analysisClipping.setDistance(this.planeZ, value);
      this.setZSliderValue(value);
      this.setZCurrentValue(value);
    });
    // Z + 버튼
    this.clippingRangeZPlusP.addEventListener("click", () => {
      let value = Number(this.clippingRangeZSlider.value) + 0.1;
      value = Number(value.toFixed(1));
      this.analysisClipping.setDistance(this.planeZ, value);
      this.setZSliderValue(value);
      this.setZCurrentValue(value);
    });
  }

  /*
   * 팝업 생성.</br> 
   * Create popup
   * @returns {Object} 생성된 팝업 요소 객체.</br> The created popup element object.
   * @example
   * # 팝업 객체
   * {
      header: {
        title: 타이틀
      },
      content: {
        subTitle: 서브타이틀
        clippingCheckList: X, Y, Z 축 체크박스 Wrapper
        clippingCheckX: X축 체크박스 Wrapper
        clippingCheckXCheckBox: X축 체크박스 Input
        clippingCheckY: Y축 체크박스 Wrapper
        clippingCheckYCheckBox: Y축 체크박스 Input
        clippingCheckZ: Z축 체크박스 Wrapper
        clippingCheckZCheckBox: Z축 체크박스 Input
        clippingRangeList: X, Y, Z 축 슬라이더 Wrapper
        clippingRangeX: X축 슬라이더 Wrapper
        clippingRangeXMinValue: X축 슬라이더 최소값 텍스트
        clippingRangeXMaxValue: X축 슬라이더 최대값 텍스트
        clippingRangeXSlider: X축 슬라이더 Input
        clippingRangeY: Y축 슬라이더 Wrapper
        clippingRangeYMinValue: Y축 슬라이더 최소값 텍스트
        clippingRangeYMaxValue: Y축 슬라이더 최대값 텍스트
        clippingRangeYSlider: Y축 슬라이더 Input
        clippingRangeZ: Z축 슬라이더 Wrapper
        clippingRangeZMinValue: Z축 슬라이더 최소값 텍스트
        clippingRangeZMaxValue: Z축 슬라이더 최대값 텍스트
        clippingRangeZSlider: Z축 슬라이더 Input
      }
    }
   */
  createInfoBox() {
    this.wrapper.classList.add(
      "ditap-popup-mobile",
      "ditap-clipping-popup",
      "ditap-mobile-bottom",
      "header-top",
      "draggable"
    );
    // header
    const title = document.createElement("h2");
    title.classList.add("ditap-popup-title");
    title.innerText = "절단면 조절";
    const closeBtn = document.createElement("button");
    closeBtn.classList.add("ditap-popup-close-btn");

    // content
    const subTitle = document.createElement("p");
    subTitle.classList.add("ditap-popup-subtitle");
    subTitle.innerText = "아래의 절단면을 조정해주세요";

    //# CheckBox Wrapper
    const clippingCheckList = document.createElement("div");
    clippingCheckList.classList.add("ditap-clipping-wrap");
    const clippingCheckX = document.createElement("button");
    clippingCheckX.classList.add("ditap-clipping-item", "ditap-custom-button");
    const clippingCheckXCheckBox = document.createElement("input");
    clippingCheckXCheckBox.classList.add("ditap-chk");
    clippingCheckXCheckBox.setAttribute("type", "checkbox");
    clippingCheckXCheckBox.checked = true;
    clippingCheckXCheckBox.setAttribute("value", "X");
    const clippingCheckXLabel = document.createElement("label");
    clippingCheckXLabel.classList.add("ditap-label");
    const clippingCheckXLabelSpan = document.createElement("span");
    clippingCheckXLabelSpan.classList.add("ditap-label-title");
    clippingCheckXLabelSpan.innerText = "X축";
    const clippingCheckY = document.createElement("button");
    clippingCheckY.classList.add("ditap-clipping-item", "ditap-custom-button");
    const clippingCheckYCheckBox = document.createElement("input");
    clippingCheckYCheckBox.classList.add("ditap-chk");
    clippingCheckYCheckBox.setAttribute("type", "checkbox");
    clippingCheckYCheckBox.checked = true;
    clippingCheckYCheckBox.setAttribute("value", "Y");
    const clippingCheckYLabel = document.createElement("label");
    clippingCheckYLabel.classList.add("ditap-label");
    const clippingCheckYLabelSpan = document.createElement("span");
    clippingCheckYLabelSpan.classList.add("ditap-label-title");
    clippingCheckYLabelSpan.innerText = "Y축";
    const clippingCheckZ = document.createElement("button");
    clippingCheckZ.classList.add("ditap-clipping-item", "ditap-custom-button");
    const clippingCheckZCheckBox = document.createElement("input");
    clippingCheckZCheckBox.classList.add("ditap-chk");
    clippingCheckZCheckBox.setAttribute("type", "checkbox");
    clippingCheckZCheckBox.checked = true;
    clippingCheckZCheckBox.setAttribute("value", "Z");
    const clippingCheckZLabel = document.createElement("label");
    clippingCheckZLabel.classList.add("ditap-label");
    const clippingCheckZLabelSpan = document.createElement("span");
    clippingCheckZLabelSpan.classList.add("ditap-label-title");
    clippingCheckZLabelSpan.innerText = "Z축";
    // clippingCheckXLabel
    clippingCheckXLabel.append(clippingCheckXLabelSpan);
    // clippingCheckX
    clippingCheckX.append(clippingCheckXCheckBox, clippingCheckXLabel);
    // clippingCheckYLabel
    clippingCheckYLabel.append(clippingCheckYLabelSpan);
    // clippingCheckY
    clippingCheckY.append(clippingCheckYCheckBox, clippingCheckYLabel);
    // clippingCheckZLabel
    clippingCheckZLabel.append(clippingCheckZLabelSpan);
    // clippingCheckZ
    clippingCheckZ.append(clippingCheckZCheckBox, clippingCheckZLabel);
    // clippingCheckList
    clippingCheckList.append(clippingCheckX, clippingCheckY, clippingCheckZ);

    //# 슬라이더 Wrapper
    const clippingRangeList = document.createElement("ul");
    clippingRangeList.classList.add("ditap-control-list");

    // 슬라이더 X
    const clippingRangeXItem = document.createElement("li");
    clippingRangeXItem.classList.add("ditap-control-item", "hide");
    const clippingRangeXHeader = document.createElement("div");
    clippingRangeXHeader.classList.add("ditap-control-header");
    const clippingRangeXMinusP = document.createElement("p");
    clippingRangeXMinusP.classList.add("ditap-control-minus");
    const clippingRangeXMinusButton = document.createElement("button");
    clippingRangeXMinusButton.classList.add("ditap-control-img");
    const clippingRangeXTitle = document.createElement("p");
    clippingRangeXTitle.classList.add("ditap-control-title");
    clippingRangeXTitle.innerText = "X축";
    const clippingRangeXPlusP = document.createElement("p");
    clippingRangeXPlusP.classList.add("ditap-control-plus");
    const clippingRangeXPlusButton = document.createElement("button");
    clippingRangeXPlusButton.classList.add("ditap-control-img");
    const clippingRangeXSlider = document.createElement("div");
    clippingRangeXSlider.classList.add("ditap-slider-item");
    const clippingRangeXSliderLabel = document.createElement("label");
    const clippingRangeXSliderInput = document.createElement("input");
    clippingRangeXSliderInput.setAttribute("type", "range");
    clippingRangeXSliderInput.setAttribute("name", "slider");
    clippingRangeXSliderInput.setAttribute("value", 0);
    clippingRangeXSliderInput.setAttribute("step", 0.1);
    clippingRangeXSliderInput.classList.add("ditap-slider");
    const clippingRangeXValue = document.createElement("div");
    clippingRangeXValue.classList.add("ditap-control-footer");
    const clippingRangeXMinValue = document.createElement("p");
    clippingRangeXMinValue.classList.add("ditap-control-value");
    const clippingRangeXCurrentValue = document.createElement("p");
    clippingRangeXCurrentValue.classList.add(
      "ditap-control-value",
      "ditap-control-center-value"
    );
    //clippingRangeXMinValue.innerText = -180;
    const clippingRangeXMaxValue = document.createElement("p");
    clippingRangeXMaxValue.classList.add(
      "ditap-control-value",
      "ditap-control-right-value"
    );
    //clippingRangeXMaxValue.innerText = 180;
    // clippingRangeXMinusP
    clippingRangeXMinusP.append(clippingRangeXMinusButton);
    // clippingRangeXPlusP
    clippingRangeXPlusP.append(clippingRangeXPlusButton);
    // clippingRangeXSliderLabel
    clippingRangeXSliderLabel.append(clippingRangeXSliderInput);
    // clippingRangeXHeader
    clippingRangeXHeader.append(
      clippingRangeXMinusP,
      clippingRangeXTitle,
      clippingRangeXPlusP
    );
    // clippingRangeXSlider
    clippingRangeXSlider.append(clippingRangeXSliderLabel);
    // clippingRangeXValue
    clippingRangeXValue.append(
      clippingRangeXMinValue,
      clippingRangeXCurrentValue,
      clippingRangeXMaxValue
    );
    // clippingRangeXItem
    clippingRangeXItem.append(
      clippingRangeXHeader,
      clippingRangeXSlider,
      clippingRangeXValue
    );

    // 슬라이더 Y
    const clippingRangeYItem = document.createElement("li");
    clippingRangeYItem.classList.add("ditap-control-item", "hide");
    const clippingRangeYHeader = document.createElement("div");
    clippingRangeYHeader.classList.add("ditap-control-header");
    const clippingRangeYMinusP = document.createElement("p");
    clippingRangeYMinusP.classList.add("ditap-control-minus");
    const clippingRangeYMinusButton = document.createElement("button");
    clippingRangeYMinusButton.classList.add("ditap-control-img");
    const clippingRangeYTitle = document.createElement("p");
    clippingRangeYTitle.classList.add("ditap-control-title");
    clippingRangeYTitle.innerText = "Y축";
    const clippingRangeYPlusP = document.createElement("p");
    clippingRangeYPlusP.classList.add("ditap-control-plus");
    const clippingRangeYPlusButton = document.createElement("button");
    clippingRangeYPlusButton.classList.add("ditap-control-img");
    const clippingRangeYSlider = document.createElement("div");
    clippingRangeYSlider.classList.add("ditap-slider-item");
    const clippingRangeYSliderLabel = document.createElement("label");
    const clippingRangeYSliderInput = document.createElement("input");
    clippingRangeYSliderInput.setAttribute("type", "range");
    clippingRangeYSliderInput.setAttribute("name", "slider");
    clippingRangeYSliderInput.setAttribute("value", 0);
    clippingRangeYSliderInput.setAttribute("step", 0.1);
    clippingRangeYSliderInput.classList.add("ditap-slider");
    const clippingRangeYValue = document.createElement("div");
    clippingRangeYValue.classList.add("ditap-control-footer");
    const clippingRangeYMinValue = document.createElement("p");
    clippingRangeYMinValue.classList.add("ditap-control-value");
    //clippingRangeYMinValue.innerText = -180;
    const clippingRangeYCurrentValue = document.createElement("p");
    clippingRangeYCurrentValue.classList.add(
      "ditap-control-value",
      "ditap-control-center-value"
    );
    const clippingRangeYMaxValue = document.createElement("p");
    clippingRangeYMaxValue.classList.add(
      "ditap-control-value",
      "ditap-control-right-value"
    );
    //clippingRangeYMaxValue.innerText = 180;
    // clippingRangeYMinusP
    clippingRangeYMinusP.append(clippingRangeYMinusButton);
    // clippingRangeYPlusP
    clippingRangeYPlusP.append(clippingRangeYPlusButton);
    // clippingRangeYSliderLabel
    clippingRangeYSliderLabel.append(clippingRangeYSliderInput);
    // clippingRangeYHeader
    clippingRangeYHeader.append(
      clippingRangeYMinusP,
      clippingRangeYTitle,
      clippingRangeYPlusP
    );
    // clippingRangeYSlider
    clippingRangeYSlider.append(clippingRangeYSliderLabel);
    // clippingRangeYValue
    clippingRangeYValue.append(
      clippingRangeYMinValue,
      clippingRangeYCurrentValue,
      clippingRangeYMaxValue
    );
    // clippingRangeYItem
    clippingRangeYItem.append(
      clippingRangeYHeader,
      clippingRangeYSlider,
      clippingRangeYValue
    );

    //슬라이더 Z
    const clippingRangeZItem = document.createElement("li");
    clippingRangeZItem.classList.add("ditap-control-item", "hide");
    const clippingRangeZHeader = document.createElement("div");
    clippingRangeZHeader.classList.add("ditap-control-header");
    const clippingRangeZMinusP = document.createElement("p");
    clippingRangeZMinusP.classList.add("ditap-control-minus");
    const clippingRangeZMinusButton = document.createElement("button");
    clippingRangeZMinusButton.classList.add("ditap-control-img");
    const clippingRangeZTitle = document.createElement("p");
    clippingRangeZTitle.classList.add("ditap-control-title");
    clippingRangeZTitle.innerText = "Z축";
    const clippingRangeZPlusP = document.createElement("p");
    clippingRangeZPlusP.classList.add("ditap-control-plus");
    const clippingRangeZPlusButton = document.createElement("button");
    clippingRangeZPlusButton.classList.add("ditap-control-img");
    const clippingRangeZSlider = document.createElement("div");
    clippingRangeZSlider.classList.add("ditap-slider-item");
    const clippingRangeZSliderLabel = document.createElement("label");
    const clippingRangeZSliderInput = document.createElement("input");
    clippingRangeZSliderInput.setAttribute("type", "range");
    clippingRangeZSliderInput.setAttribute("name", "slider");
    clippingRangeZSliderInput.setAttribute("value", 0);
    clippingRangeZSliderInput.setAttribute("step", 0.1);
    clippingRangeZSliderInput.classList.add("ditap-slider");
    const clippingRangeZValue = document.createElement("div");
    clippingRangeZValue.classList.add("ditap-control-footer");
    const clippingRangeZMinValue = document.createElement("p");
    clippingRangeZMinValue.classList.add("ditap-control-value");
    //clippingRangeZMinValue.innerText = -180;
    const clippingRangeZCurrentValue = document.createElement("p");
    clippingRangeZCurrentValue.classList.add(
      "ditap-control-value",
      "ditap-control-center-value"
    );
    const clippingRangeZMaxValue = document.createElement("p");
    clippingRangeZMaxValue.classList.add(
      "ditap-control-value",
      "ditap-control-right-value"
    );
    //clippingRangeZMaxValue.innerText = 180;
    // clippingRangeZMinusP
    clippingRangeZMinusP.append(clippingRangeZMinusButton);
    // clippingRangeZPlusP
    clippingRangeZPlusP.append(clippingRangeZPlusButton);
    // clippingRangeZSliderLabel
    clippingRangeZSliderLabel.append(clippingRangeZSliderInput);
    // clippingRangeZHeader
    clippingRangeZHeader.append(
      clippingRangeZMinusP,
      clippingRangeZTitle,
      clippingRangeZPlusP
    );
    // clippingRangeZSlider
    clippingRangeZSlider.append(clippingRangeZSliderLabel);
    // clippingRangeZValue
    clippingRangeZValue.append(
      clippingRangeZMinValue,
      clippingRangeZCurrentValue,
      clippingRangeZMaxValue
    );
    // clippingRangeZItem
    clippingRangeZItem.append(
      clippingRangeZHeader,
      clippingRangeZSlider,
      clippingRangeZValue
    );

    // clippingRangeList
    clippingRangeList.append(
      clippingRangeXItem,
      clippingRangeYItem,
      clippingRangeZItem
    );

    return {
      header: {
        title: title,
      },
      content: {
        subTitle: subTitle,

        clippingCheckList: clippingCheckList,
        clippingCheckX: clippingCheckX,
        clippingCheckXCheckBox: clippingCheckXCheckBox,
        clippingCheckY: clippingCheckY,
        clippingCheckYCheckBox: clippingCheckYCheckBox,
        clippingCheckZ: clippingCheckZ,
        clippingCheckZCheckBox: clippingCheckZCheckBox,

        clippingRangeList: clippingRangeList,
        clippingRangeX: clippingRangeXItem,
        clippingRangeXMinValue: clippingRangeXMinValue,
        clippingRangeXCurrentValue: clippingRangeXCurrentValue,
        clippingRangeXMaxValue: clippingRangeXMaxValue,
        clippingRangeXSlider: clippingRangeXSliderInput,
        clippingRangeXMinusP: clippingRangeXMinusP,
        clippingRangeXPlusP: clippingRangeXPlusP,
        clippingRangeY: clippingRangeYItem,
        clippingRangeYMinValue: clippingRangeYMinValue,
        clippingRangeYCurrentValue: clippingRangeYCurrentValue,
        clippingRangeYMaxValue: clippingRangeYMaxValue,
        clippingRangeYSlider: clippingRangeYSliderInput,
        clippingRangeYMinusP: clippingRangeYMinusP,
        clippingRangeYPlusP: clippingRangeYPlusP,
        clippingRangeZ: clippingRangeZItem,
        clippingRangeZMinValue: clippingRangeZMinValue,
        clippingRangeZCurrentValue: clippingRangeZCurrentValue,
        clippingRangeZMaxValue: clippingRangeZMaxValue,
        clippingRangeZSlider: clippingRangeZSliderInput,
        clippingRangeZMinusP: clippingRangeZMinusP,
        clippingRangeZPlusP: clippingRangeZPlusP,
      },
    };

    // `
    //   <div class="ditap-popup-header">
    //     <h2 class="ditap-popup-title">절단면 조정</h2> -- title
    //     <button class="ditap-popup-close-btn"></button> -- closeBtn
    //   </div>
    //   <div class="ditap-popup-content">
    //     <p class="ditap-popup-subtitle">아래의 절단면을 조정해주세요</p> -- subTitle
    //     <div class="ditap-clipping-wrap"> -- clippingCheckList
    //       <p class="ditap-clipping-item"> -- clippingCheckX
    //         <input type="checkbox" class="ditap-chk" id="X" /> -- clippingCheckXCheckBox
    //         <label class="ditap-label" for="X"> -- clippingCheckXLabel
    //           <span class="ditap-label-title">X축</span> -- clippingCheckXLabelSpan
    //         </label>
    //       </p>
    //       <p class="ditap-clipping-item"> -- clippingCheckY
    //         <input type="checkbox" class="ditap-chk" id="Y" /> -- clippingCheckYCheckBox
    //         <label class="ditap-label" for="Y"> -- clippingCheckYLabel
    //           <span class="ditap-label-title">Y축</span> -- clippingCheckYLabelSpan
    //         </label>
    //       </p>
    //       <p class="ditap-clipping-item"> -- clippingCheckZ
    //         <input type="checkbox" class="ditap-chk" id="Z" /> -- clippingCheckZCheckBox
    //         <label class="ditap-label" for="Z"> -- clippingCheckZLabel
    //           <span class="ditap-label-title">Z축</span> -- clippingCheckZLabelSpan
    //         </label>
    //       </p>
    //     </div>
    //     <ul class="ditap-control-list"> -- clippingRangeList
    //       <li class="ditap-control-item"> -- clippingRangeX
    //         <div class="ditap-control-header"> -- clippingRangeXHeader
    //           <p class="ditap-control-value">-180</p> -- clippingRangeXMinValue
    //           <p class="ditap-control-title">X축 범위</p> -- clippingRangeXTitle
    //           <p class="ditap-control-value">180</p> -- clippingRangeXMaxValue
    //         </div>
    //         <div class="ditap-slider-item"> -- clippingRangeXContent
    //           <label for="slider5" -- clippingRangeXLabel
    //             ><input -- clippingRangeXSlider
    //               id="slider5"
    //               oninput="slider(this)"
    //               class="ditap-slider"
    //               type="range"
    //               min="0"
    //               max="100"
    //               name="slider"
    //           /></label>
    //         </div>
    //       </li>
    //       <li class="ditap-control-item"> -- clippingRangeY
    //         <div class="ditap-control-header"> -- clippingRangeYHeader
    //           <p class="ditap-control-value">-180</p> -- clippingRangeYMinValue
    //           <p class="ditap-control-title">Y축 범위</p> -- clippingRangeYTitle
    //           <p class="ditap-control-value">180</p> -- clippingRangeYMaxValue
    //         </div>
    //         <div class="ditap-slider-item"> -- clippingRangeYContent
    //           <label for="slider6" -- clippingRangeYLabel
    //             ><input -- clippingRangeYSlider
    //               id="slider6"
    //               oninput="slider(this)"
    //               class="ditap-slider"
    //               type="range"
    //               min="0"
    //               max="100"
    //               name="slider"
    //           /></label>
    //         </div>
    //       </li>
    //       <li class="ditap-control-item"> -- clippingRangeZ
    //         <div class="ditap-control-header"> -- clippingRangeZHeader
    //           <p class="ditap-control-value">-180</p> -- clippingRangeZMinValue
    //           <p class="ditap-control-title">Z축 범위</p> -- clippingRangeZTitle
    //           <p class="ditap-control-value">180</p> -- clippingRangeZMaxValue
    //         </div>
    //         <div class="ditap-slider-item"> -- clippingRangeZContent
    //           <label for="slider7" -- clippingRangeZLabel
    //             ><input -- clippingRangeZSlider
    //               id="slider7"
    //               oninput="slider(this)"
    //               class="ditap-slider"
    //               type="range"
    //               min="0"
    //               max="100"
    //               name="slider"
    //           /></label>
    //         </div>
    //       </li>
    //     </ul>
    //   </div>
    // `
  }
}

export default PopupAnalysisClipping;
